import { RawAxiosRequestConfig } from 'axios';
import { StatusCodes } from 'http-status-codes';
import { ActionContext } from 'vuex';
import { ProductAPIQueryBuilder } from '@api/builders/product';
import getAPIProduct from '@api/product';
import { HttpRejection } from '@model/http/rejection';
import { Logger } from '@utils/logger';

const FIELDS_EXCEPT = [
    'created_by',
    'created_at',
    'updated_at',
    'is_active',
    'is_active_since',
    'is_active_until',
    'locale',
    'name',
    'product',
];

export default {
    namespaced: true,

    state: () => ({
        data: [],
    }),

    actions: {

        async getPromotionTranslation(store: ActionContext<string, any>, args: { locale: string, id: string, discount: string }) {
            const client = getAPIProduct(this.$context);

            const request = new ProductAPIQueryBuilder('translation')
                .setEntityPath('/api/finance/promotions/translations/')
                .addMatchesAll('locale', '=', args.locale)
                .addMatchesAll('promotion_id', '=', args.id)
                .setOutputExcept(['id', ...FIELDS_EXCEPT])
                .toObject();

            const cfg: RawAxiosRequestConfig = {
                validateStatus: (status: StatusCodes) => status === StatusCodes.OK,
                timeout: typeof window !== 'undefined' ? 30000 : 6000,
                method: 'GET',
                url: request.entityPath,
                params: request.params,
            };

            try {
                const response = await client.request(cfg);
                const translation = response?.data?.data?.[0] || {};

                if (args.discount) {
                    Object.entries(translation).forEach(([key, value]) => {
                        if (typeof value !== 'string') return;
                        translation[key] = value.replaceAll('XX', args.discount);
                    });
                }

                store.commit('setPromotionTranslation', translation);
            } catch (error) {
                Logger.error({ error });
                const httpStatus = error.status || StatusCodes.GATEWAY_TIMEOUT;
                const message = error.message || 'Premature rendering stop';
                throw new HttpRejection(message, httpStatus);
            }
        },

        async getPromotions(store: ActionContext<string, any>, locale: string) {
            const now = new Date().toISOString();

            const req = new ProductAPIQueryBuilder('promotions')
                .addMatchesAll('is_active', '=', '1')
                .addMatchesAll('is_active_since', '<=', now)
                .addMatchesAll('is_active_until', '>=', now)
                .setCustomParam('locales', [locale])
                .setOutputExcept(FIELDS_EXCEPT)
                .addSort('created_at', 'desc')
                .addSort('id', 'desc');

            const options: RawAxiosRequestConfig = {
                method: 'GET',
                url: '/api/finance/promotions/',
                timeout: 2000,
                validateStatus: (status: StatusCodes) => status === StatusCodes.OK,
                params: req.toObject().params,
            };

            try {
                const response = await getAPIProduct(this.$context).request(options);
                const promotions = response.data.data
                    .map((promotion: any) => {
                        const list = promotion.discounts || '[]';
                        const discounts = JSON.parse(list).map((item: any) => ({
                            list: item?.productList?.products.map((product: any) => product.id),
                            discount: item?.discount?.pricePercentageValues?.[0]?.value || 0,
                            maxQuantity: Number(item?.discount?.maxQuantity) || 0,
                        }));
                        return {
                            ...promotion,
                            discounts,
                            license_ids: discounts.map((item: any) => item.list).flat(),
                            discount: Math.max(0, ...discounts.map((item: any) => item.discount)),
                        };
                    });

                store.commit('setPromotions', promotions);

                const promises = promotions.map((item: any) => {
                    const id = item.id;
                    const discount = item.discount;
                    return store.dispatch('getPromotionTranslation', { locale, id, discount });
                });
                await Promise.all(promises);
            } catch (error) {
                Logger.error({ error });
            }
        },
    },

    mutations: {
        setPromotions(state: any, promotions = []) {
            state.data = promotions;
        },
        setPromotionTranslation(state: any, translation: any) {
            state.data = state.data.map((item: any) => {
                if (item.id !== translation.promotion_id) return item;
                return { ...item, promoSettings: translation };
            });
        },
    },
};
